import { Component, computed, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  EmptyStateComponent,
  IButtonComponent,
  IInputComponent,
  MODAL_DATA,
  ModalRef,
  ModalService,
} from "@front/m19-ui";
import { SegmentConfigType, StrategyEx } from "@front/m19-models";
import { StrategyGroupEx } from "@front/m19-models";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { CampaignType, StrategyTactic, TacticType } from "@front/m19-api-client";
import { AccountSelectionService, SegmentService } from "@front/m19-services";
import { switchMap } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { TacticComponent } from "@m19-board/strategies/tactic/tactic.component";
import { TacticAddPopupComponent } from "@m19-board/strategies/strategy-page/tactic-add-popup.component";
import { TacticAddPopupModalData } from "@m19-board/strategies/strategy-page/tactic-add-popup.component";

export interface StrategyTacticModalData {
  strategy: StrategyEx;
  isReadOnly: boolean;
  tactics: StrategyTactic[];
  tacticType: TacticType;
  strategyGroup: StrategyGroupEx;
}

@Component({
  selector: "strategy-tactic-modal",
  standalone: true,
  imports: [CommonModule, TranslocoDirective, IInputComponent, IButtonComponent, TacticComponent, EmptyStateComponent],
  templateUrl: "./strategy-tactic-modal.component.html",
})
export class StrategyTacticModalComponent {
  private readonly segmentService = inject(SegmentService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);

  readonly TacticType = TacticType;

  readonly modalRef = inject(ModalRef);
  readonly modalData = inject(MODAL_DATA) as StrategyTacticModalData;

  tactics = signal<StrategyTactic[]>(this.modalData.tactics);
  filteredTactics = computed(() =>
    this.tactics().filter((t) => this.segmentIndex()?.get(t.segmentId)?.name.includes(this.tacticSearchInput())),
  );

  segmentIndex = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
    ),
  );

  tacticSearchInput = signal("");

  createTactic(): void {
    const allowedSegmentTacticTypes: [SegmentConfigType, TacticType][] = [];
    if (this.modalData.tacticType == TacticType.LEGACY)
      allowedSegmentTacticTypes.push([SegmentConfigType.KeywordSegment, TacticType.LEGACY]);
    else allowedSegmentTacticTypes.push([SegmentConfigType.KeywordSegment, TacticType.BLACKLIST]);

    if (this.modalData.strategy.productTargetingEnabled) {
      if (this.modalData.tacticType == TacticType.LEGACY)
        allowedSegmentTacticTypes.push([SegmentConfigType.ProductSegment, TacticType.LEGACY]);
      else allowedSegmentTacticTypes.push([SegmentConfigType.ProductSegment, TacticType.BLACKLIST]);
    }

    const ref = this.modalService.openModal<TacticAddPopupModalData, StrategyTactic>(TacticAddPopupComponent, {
      modalTitle: this.translocoService.translate("strategy-page.tactics"),
      data: { strategy: this.modalData.strategy, allowedSegmentTacticTypes },
    });

    ref.afterClosed().subscribe((tactic) => {
      if (tactic) {
        this.tactics.update((tactics) => [...tactics, tactic]);
      }
    });
  }

  tacticDeleted(tactic: StrategyTactic): void {
    this.tactics.update((tactics) => tactics.filter((t) => t.segmentId !== tactic.segmentId));
  }
}
